import { maxTime } from 'date-fns/constants';

import {
  EventMarket,
  EventOutcome,
  EventResponse,
  LiveEvent,
  Market,
  MarketIcon,
  Outcome,
} from '@/types';
import { formatSportPrice, formatVolume } from './formatters';

/**
 * Extracts the player name from a player prop title.
 * e.g. "LaMelo Ball: Points O/U 19.5" -> "LaMelo Ball"
 */
export const extractPlayerName = (str: string): string => {
  if (!str) return '';
  return str.split(':')[0].trim();
};

/**
 * Determines if a game has reached its final/terminal state.
 */
export const isGameEnded = (
  ended?: boolean,
  status?: string,
  score?: string,
  isLive?: boolean,
): boolean => {
  if (ended === true) return true;
  const s = status?.toLowerCase() || '';
  if (s === 'final' || s === 'ended' || s === 'closed') return true;
  // If we have a score and it's not live, treat as ended in historical contexts
  if (score && isLive === false) return true;
  return false;
};

/**
 * Checks if a game is a tennis game based on its tags.
 */
export const isTennisGame = (game: any): boolean => {
  if (!game?.tags || !Array.isArray(game.tags)) return false;
  return game.tags.some(
    (tag) => tag.label?.toLowerCase() === 'tennis' || tag.slug?.toLowerCase() === 'tennis',
  );
};

/**
 * Calculates the winner of a game based on scores.
 */
export const getGameWinner = (
  homeScore: string | number,
  awayScore: string | number,
  homeTeam: string,
  awayTeam: string,
): string => {
  const h = typeof homeScore === 'number' ? homeScore : parseInt(homeScore || '0');
  const a = typeof awayScore === 'number' ? awayScore : parseInt(awayScore || '0');

  if (h > a) return `${homeTeam} wins`;
  if (a > h) return `${awayTeam} wins`;
  return 'Draw';
};

/**
 * Parses a price string into a numeric representation.
 * Handles strings like "50¢", "$50", and "1k".
 */
export const parsePrice = (priceStr: string): number => {
  const numericValue = parseFloat(priceStr.replace('¢', '').replace('$', '').replace('k', '000'));
  return numericValue / 100;
};

/**
 * Safely extracts outcomes from a market object or JSON string.
 */
export const getOutcomes = (market?: EventMarket): EventOutcome[] => {
  if (!market) return [];
  try {
    return typeof market.outcomes === 'string'
      ? JSON.parse(market.outcomes)
      : market.outcomes || [];
  } catch (e) {
    return [];
  }
};

/**
 * Maps a moneyline outcome to a display team when Gamma outcome order differs from home/away
 * (common for cricket, tennis, and some slug/team enrichments).
 */
export const findOutcomeForTeamName = (
  outcomes: EventOutcome[],
  teamName: string | undefined | null,
  fallbackIndex: number,
): EventOutcome | undefined => {
  if (!outcomes?.length) return undefined;
  const safeIdx = Math.min(Math.max(fallbackIndex, 0), outcomes.length - 1);
  const fb = outcomes[safeIdx];
  if (!teamName?.trim()) return fb;

  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const team = norm(teamName);
  const teamWords = team.split(' ').filter((w) => w.length > 2);

  let best: EventOutcome | undefined;
  let bestScore = 0;

  for (const o of outcomes) {
    const on = norm(o.name || '');
    if (!on) continue;
    if (on === team) {
      return o;
    }
    let score = 0;
    if (on.includes(team) || team.includes(on)) {
      score = 50;
    } else {
      for (const w of teamWords) {
        if (w.length > 2 && on.includes(w)) score += 12;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = o;
    }
  }

  return bestScore >= 12 ? best : fb;
};

/**
 * Resolves home/away moneyline rows for two-outcome markets using team names (not raw indices).
 */
export const pairTwoWayMoneylineOutcomes = (
  outcomes: EventOutcome[],
  homeTeamName?: string | null,
  awayTeamName?: string | null,
): { home?: EventOutcome; away?: EventOutcome } => {
  if (!outcomes?.length) return {};
  if (outcomes.length === 1) {
    return { home: outcomes[0] };
  }
  const h = findOutcomeForTeamName(outcomes, homeTeamName, 0);
  if (outcomes.length === 2) {
    const other = outcomes.find((o) => o.id !== h?.id) || outcomes[1];
    const a = findOutcomeForTeamName(outcomes, awayTeamName, 1);
    if (h && a && h.id === a.id) {
      return { home: h, away: other };
    }
    return { home: h, away: a?.id === h?.id ? other : a };
  }
  return {
    home: outcomes[0],
    away: outcomes[1],
  };
};

/**
 * Single line value for spread/total markets: prefer `line`, else parse `groupItemThreshold`.
 */
export const getCanonicalMarketLine = (
  market?: EventMarket | Partial<EventMarket>,
): string | number | undefined => {
  if (!market) return undefined;
  const rawLine = market.line;
  if (rawLine !== undefined && rawLine !== null && String(rawLine).trim() !== '') {
    const n = Number(rawLine);
    return Number.isFinite(n) ? n : rawLine;
  }
  const th = market.groupItemThreshold;
  if (th === undefined || th === null || String(th).trim() === '') return undefined;
  const n = parseFloat(String(th).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : th;
};

/**
 * Finds a specific market type (moneyline, spread, total) within an event.
 */
export const findMarket = (event: EventResponse, type: string) => {
  const normalizedType = type?.toLowerCase();
  const markets = event.markets || [];

  // First attempt: matching exactly with the normalizedType
  const primaryMatch = markets.find((m: EventMarket) => {
    return getMarketType(m) === normalizedType;
  });

  if (primaryMatch) return primaryMatch;

  // Generic spread/total requests should prefer the main game-line types.
  // Without this, we may accidentally pick first_half_spreads before spreads
  // when API ordering differs across events.
  if (normalizedType === 'spread' || normalizedType === 'spreads') {
    return (
      markets.find((m: EventMarket) => getMarketType(m) === 'spreads') ||
      markets.find((m: EventMarket) => getMarketType(m) === 'spread') ||
      markets.find((m: EventMarket) => getMarketType(m).includes('spread'))
    );
  }
  if (normalizedType === 'total' || normalizedType === 'totals') {
    return (
      markets.find((m: EventMarket) => getMarketType(m) === 'totals') ||
      markets.find((m: EventMarket) => getMarketType(m) === 'total') ||
      markets.find((m: EventMarket) => getMarketType(m).includes('total'))
    );
  }

  // Second attempt: matching with aliases
  return markets.find((m: EventMarket) => {
    const mType = getMarketType(m);
    const gTitle = (m.groupItemTitle || '')?.toLowerCase();

    if (normalizedType.includes('moneyline')) {
      return mType.includes('moneyline');
    }

    if (normalizedType.includes('spread')) {
      return mType.includes('spread');
    }

    if (normalizedType.includes('draw')) {
      return (
        mType.includes('moneyline') &&
        (gTitle.includes('draw') || gTitle.includes('empate') || gTitle.includes('dibujar'))
      );
    }

    if (normalizedType.includes('spread')) {
      return mType.includes('spread');
    }

    if (normalizedType.includes('total')) {
      return mType.includes('total');
    }

    return mType === normalizedType;
  });
};

/**
 * Finds a specific market type with a specific line/threshold.
 */
export const findMarketByLine = (event: EventResponse, type: string, line: string | number) => {
  const normalizedType = type?.toLowerCase();
  const markets = event.markets || [];
  const lineStr = String(line);

  const lineMatch = (m: EventMarket) => {
    const canon = getCanonicalMarketLine(m);
    const mLine = canon === undefined || canon === null ? '' : String(canon);
    return mLine === lineStr;
  };

  const exact = markets.find(
    (m: EventMarket) => getMarketType(m) === normalizedType && lineMatch(m),
  );
  if (exact) return exact;

  const isGenericSpread = normalizedType === 'spread' || normalizedType === 'spreads';
  const isGenericTotal = normalizedType === 'total' || normalizedType === 'totals';

  return markets.find((m: EventMarket) => {
    const mType = getMarketType(m);
    const isCorrectType =
      (isGenericSpread && mType.includes('spread')) || (isGenericTotal && mType.includes('total'));
    return isCorrectType && lineMatch(m);
  });
};

/**
 * Transforms an EventResponse into a Market/Outcome pair for trading.
 */
/**
 * Robustly finds the index of an outcome that matches a team name.
 * Handles cases like "Philadelphia 76ers" vs "76ers" or partial matches.
 */
export const findOutcomeIndexForTeam = (outcomes: EventOutcome[], teamName?: string): number => {
  if (!teamName || outcomes.length === 0) return -1;

  const normalize = (s: string) =>
    String(s || '')
      ?.toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const teamNorm = normalize(teamName);
  if (!teamNorm) return -1;

  // 1. Exact or contains match
  let idx = outcomes.findIndex((o) => {
    const on = normalize(o.name);
    return on === teamNorm || on.includes(teamNorm) || teamNorm.includes(on);
  });
  if (idx !== -1) return idx;

  // 2. Token-based match (e.g., "Lakers" in "Los Angeles Lakers")
  const tokens = teamNorm.split(' ').filter((t) => t.length >= 3);
  if (tokens.length === 0) return -1;

  idx = outcomes.findIndex((o) => {
    const on = normalize(o.name);
    return tokens.some((t) => on.includes(t));
  });

  return idx;
};

export const createMarketFromGame = (
  event: EventResponse,
  teamIndex: 1 | 2,
  sportSlug: string,
  isPreviousMarket?: boolean,
): { market: Market; outcome: Outcome; teamColor: string } => {
  const team = teamIndex === 1 ? event.homeTeam : event.awayTeam;
  const tNameLower = team?.name?.toLowerCase().trim() || '';

  const specificMarket =
    event.markets?.find((m) => {
      const type = getMarketType(m);
      const title = ((m as any).groupItemTitle || '')?.toLowerCase();
      return (
        type.includes('moneyline') &&
        tNameLower &&
        title.includes(tNameLower) &&
        !title.includes('draw')
      );
    }) || findMarket(event, 'moneyline');

  const mlOutcomes = getOutcomes(specificMarket);
  const mlOutcome = mlOutcomes.find((o) => o?.name?.toLowerCase().trim() === tNameLower);

  // Determine the real index of the clicked team's outcome in the market's outcomes array.
  // This is critical for sports like UFC where outcome order in the API may not match
  // the home/away (teamIndex) convention used by the card UI.
  const mlOutcomeRealIndex = mlOutcome
    ? mlOutcomes.findIndex((o) => o?.id === mlOutcome.id)
    : teamIndex - 1; // fallback: assume index = teamIndex - 1

  const opponent = teamIndex === 1 ? event.awayTeam : event.homeTeam;

  let marketTitle = '';
  if (team?.name && opponent?.name) {
    marketTitle = `${team.name} vs ${opponent.name}`;
  } else if (event.title) {
    marketTitle = event.title;
  } else {
    marketTitle =
      (specificMarket as any)?.question ||
      (specificMarket as any)?.groupItemTitle ||
      (specificMarket as any)?.title ||
      `${team?.name || 'Home'} vs ${opponent?.name || 'Away'}`;
  }

  const teamColor = team?.color || (teamIndex === 1 ? '#e8b046' : '#c24632');

  const isEnded =
    isPreviousMarket || isGameEnded(event.ended, event.status, event.score, event.isLive);
  let status: 'open' | 'resolved' | 'settled' = 'open';
  let winnerIndex: number | null = null;
  let outcomePrice = 0;

  if (isEnded) {
    status = 'resolved';
    const parsedScore = parseLiveScore(event.score);
    const homeScore = parseInt(parsedScore?.home || event.homeTeam?.score || '0');
    const awayScore = parseInt(parsedScore?.away || event.awayTeam?.score || '0');

    if (homeScore > awayScore) winnerIndex = 0;
    else if (awayScore > homeScore) winnerIndex = 1;

    // For the virtualized "Yes/No" market created for the clicked team:
    // If the clicked team won, price is 1. If lost, price is 0.
    const clickedTeamWon =
      (teamIndex === 1 && winnerIndex === 0) || (teamIndex === 2 && winnerIndex === 1);
    outcomePrice = clickedTeamWon ? 1 : 0;
  } else {
    outcomePrice = parsePrice(
      // Use mlOutcome (name-matched) first, then fall back to the real index
      // (mlOutcomeRealIndex) — NOT teamIndex-1, which would be wrong for sports
      // like UFC where API outcome order doesn't follow the home/away convention.
      formatSportPrice(mlOutcome?.price || mlOutcomes[mlOutcomeRealIndex]?.price),
    );
  }

  // Pull clobTokenIds & negRisk from the real moneyline market returned by the API.
  // IMPORTANT: Reorder tokens so the clicked team's token is always at index 0 (YES/first
  // position). Without this, sports like UFC—where API outcome order differs from home/away
  // convention—would load the opponent's token prices into the sidebar instead of the clicked
  // fighter's prices.
  let clobTokenIds: string[] = [];
  try {
    const raw = specificMarket?.clobTokenIds;
    let rawTokenIds: string[] = [];
    if (Array.isArray(raw)) {
      rawTokenIds = raw;
    } else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
      rawTokenIds = JSON.parse(raw);
    } else if (raw) {
      rawTokenIds = [String(raw)];
    }

    // If the clicked team's outcome is NOT at index 0 in the market, reorder tokens.
    if (mlOutcomeRealIndex > 0 && rawTokenIds.length > mlOutcomeRealIndex) {
      const clickedToken = rawTokenIds[mlOutcomeRealIndex];
      const others = [
        ...rawTokenIds.slice(0, mlOutcomeRealIndex),
        ...rawTokenIds.slice(mlOutcomeRealIndex + 1),
      ];
      clobTokenIds = [clickedToken, ...others];
    } else {
      clobTokenIds = rawTokenIds;
    }
  } catch (_) {
    clobTokenIds = [];
  }

  const negRisk: boolean = (specificMarket as any)?.negRisk ?? false;
  const drawMarket = findMarket(event, 'draw');
  const hasDraw = !!drawMarket;

  const market: Market = {
    id: `${event.id}-${teamIndex}`,
    title: marketTitle,
    category: 'Sports',
    status: status,
    endsAt: event.endDate || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    groupItemTitle: team?.name || (teamIndex === 1 ? 'Home' : 'Away'),
    outcomes: [
      {
        id: `${event.id}-${teamIndex}-yes`,
        name: hasDraw ? 'Yes' : team?.name || (teamIndex === 1 ? 'Home' : 'Away'),
        price: outcomePrice,
        liquidity: 1000,
        volume: 0,
      },
      {
        id: `${event.id}-${teamIndex}-no`,
        name: hasDraw ? 'No' : opponent?.name || (teamIndex === 1 ? 'Away' : 'Home'),
        price: 1 - outcomePrice,
        liquidity: 1000,
        volume: 0,
      },
    ],
    icon: (event.icon as MarketIcon) || 'sports',
    clobTokenIds,
    negRisk,
  };

  const outcome: Outcome = market.outcomes[0];

  return { market, outcome, teamColor };
};

export const mapEventToLiveEvent = (event: EventResponse): LiveEvent => {
  const moneylineMarket = findMarket(event, 'moneyline');
  const spreadsMarket = findMarket(event, 'spread');
  const totalsMarket = findMarket(event, 'total');

  const mlOutcomes = getOutcomes(moneylineMarket);
  const spreadOutcomes = getOutcomes(spreadsMarket);
  const totalOutcomes = getOutcomes(totalsMarket);

  return {
    id: event.id,
    league: event.series?.[0]?.title || event.seriesSlug?.replace(/-/g, ' ') || 'Other',
    isLive: event.isLive,
    status: event.period || 'Upcoming',
    volume: `${formatVolume(event.volume)} Vol.`,
    gameViewCount: event.markets?.length || 0,
    sportSlug: event.seriesSlug || 'live',
    team1: {
      name: event.homeTeam?.name || 'Home',
      abbr: event.homeTeam?.abbreviation || 'HME',
      score:
        typeof event.homeTeam?.score === 'number'
          ? event.homeTeam.score
          : parseInt(event.homeTeam?.score || '0'),
      color: event.homeTeam?.color || '#e8b046',
      logo: event.homeTeam?.logo,
    },
    team2: {
      name: event.awayTeam?.name || 'Away',
      abbr: event.awayTeam?.abbreviation || 'AWY',
      score:
        typeof event.awayTeam?.score === 'number'
          ? event.awayTeam.score
          : parseInt(event.awayTeam?.score || '0'),
      color: event.awayTeam?.color || '#c24632',
      logo: event.awayTeam?.logo,
    },
    price1: formatSportPrice(mlOutcomes[0]?.price),
    price2: formatSportPrice(mlOutcomes[1]?.price),
    spread1:
      spreadsMarket && spreadOutcomes.length >= 2
        ? {
            label: `${event.homeTeam?.abbreviation || 'HME'} ${spreadsMarket.groupItemThreshold}`,
            price: formatSportPrice(spreadOutcomes[0]?.price),
          }
        : undefined,
    spread2:
      spreadsMarket && spreadOutcomes.length >= 2
        ? {
            label: `${event.awayTeam?.abbreviation || 'AWY'} ${spreadsMarket.groupItemThreshold}`,
            price: formatSportPrice(spreadOutcomes[1]?.price),
          }
        : undefined,
    totalOver:
      totalsMarket && totalOutcomes.length >= 2
        ? {
            label: `O ${totalsMarket.groupItemThreshold}`,
            price: formatSportPrice(totalOutcomes[0]?.price),
          }
        : undefined,
    totalUnder:
      totalsMarket && totalOutcomes.length >= 2
        ? {
            label: `U ${totalsMarket.groupItemThreshold}`,
            price: formatSportPrice(totalOutcomes[1]?.price),
          }
        : undefined,
  };
};

/**
 * Parses complex live score strings returned by the API.
 * Examples of input: "0-0", "00-00|2-1|Bo5"
 * The expected match score is usually the segment containing a hyphen (that is not purely zeroes or a time).
 * We take the last segment containing a hyphen that looks like a score.
 */
export const parseLiveScore = (scoreStr?: string): { home: string; away: string } | null => {
  if (!scoreStr) return null;

  // Split by pipe to handle formats like "000-000|2-0|Bo5"
  const segments = scoreStr.split('|');

  // We are looking for the segment that contains the actual score (e.g. "2-0")
  // Typically it's a segment with a hyphen, which is not just zero padding.
  // Alternatively, just taking the first segment that fits the pattern `\d+-\d+`.

  // If there's a pipe, the match score is usually the second segment, or the last one with a hyphen.
  // Let's iterate backwards to find the first one that clearly has a hyphen separating two strings/numbers
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i].trim();
    if (seg.includes('-') && !seg?.toLowerCase().includes('bo')) {
      const parts = seg.split('-');
      if (parts.length >= 2) {
        return {
          home: parts[0].trim(),
          away: parts[1].trim(),
        };
      }
    }
  }

  // Fallback: just split the whole string if no pipe, or if iteration failed
  if (scoreStr.includes('-')) {
    const parts = scoreStr.split('-');
    if (parts.length >= 2) {
      return {
        home: parts[0].trim(),
        away: parts[1].trim(),
      };
    }
  }

  return null;
};

/**
 * Finds a market that contains a specific outcome ID.
 */
export const findMarketByOutcomeId = (
  markets: EventMarket[] = [],
  outcomeId: string | undefined,
): EventMarket | undefined => {
  if (!outcomeId) return undefined;
  return markets.find((m) => {
    const outcomes = typeof m?.outcomes === 'string' ? JSON.parse(m.outcomes) : m?.outcomes || [];
    return outcomes?.some((o: any) => o?.id === outcomeId);
  });
};

/**
 * Checks if a specific market type is currently active based on the selected outcome ID.
 */
export const checkMarketActive = (
  markets: EventMarket[] = [],
  activeOutcomeId: string | undefined,
  typeKey: string,
): boolean => {
  const market = findMarketByOutcomeId(markets, activeOutcomeId);
  if (!market) return false;

  return getMarketType(market).includes(typeKey);
};

/**
 * Extracts unique market lines (spreads and totals) from a list of markets.
 */
export const extractUniqueLines = (
  markets: EventMarket[] = [],
  isLive: boolean = false,
): { spreadLines: (string | number)[]; totalLines: (string | number)[] } => {
  const sLines = new Set<string | number>();
  const tLines = new Set<string | number>();

  markets.forEach((m) => {
    const type = getMarketType(m);
    const threshold = m?.line;
    if (threshold === undefined || threshold === null || String(threshold) === '') return;

    // Normalize to number if possible to avoid string vs number duplicates
    const numValue = Number(threshold);
    const normalizedThreshold = isNaN(numValue) ? threshold : numValue;

    if (type.includes('spread')) {
      sLines.add(normalizedThreshold);
    } else if (type.includes('total')) {
      tLines.add(normalizedThreshold);
    }
  });

  return {
    spreadLines: Array.from(sLines).sort((a, b) => Number(a) - Number(b)),
    totalLines: Array.from(tLines).sort((a, b) => Number(a) - Number(b)),
  };
};

/**
 * Checks if any outcome in the list has a valid price.
 */
export const hasValidPrice = (outcomes: any[] = []): boolean => {
  return outcomes.some(
    (o) =>
      (o?.price !== null && o?.price !== undefined && o?.price !== '') ||
      (o?.bestBid !== null && o?.bestBid !== undefined && o?.bestBid !== 0) ||
      (o?.bestAsk !== null && o?.bestAsk !== undefined && o?.bestAsk !== 0),
  );
};

/**
 * Calculates the total aggregated volume for a specific market category.
 */
export const calculateCategoryVolume = (
  markets: EventMarket[] = [],
  marketType: string,
): number => {
  const lowerType = marketType?.toLowerCase();
  const isML = lowerType === 'moneyline';
  const isSpread = lowerType === 'spread' || lowerType === 'spreads';
  const isTotal = lowerType === 'total' || lowerType === 'totals';

  return markets.reduce((acc, m) => {
    const type = getMarketType(m);
    const isMatch =
      (isML && type === 'moneyline') ||
      (isSpread && (type.includes('spread') || type === 'handicap' || type === 'puck line')) ||
      (isTotal && (type.includes('total') || type === 'over/under')) ||
      (!isML && !isSpread && !isTotal && type === lowerType);

    return isMatch ? acc + Number(m.volumeNum || m.volume || 0) : acc;
  }, 0);
};

/**
 * Safely extracts and normalizes the sports market type.
 */
export const getMarketType = (market?: EventMarket | Partial<EventMarket>): string => {
  return (market?.sportsMarketType || market?.sportsType || '')?.toLowerCase();
};

/**
 * Safely extracts clobTokenIds from a market.
 */
export const getMarketTokenIds = (market?: EventMarket): string[] => {
  if (!market || !market.clobTokenIds) return [];
  try {
    if (Array.isArray(market.clobTokenIds)) return market.clobTokenIds;
    if (typeof market.clobTokenIds === 'string') {
      if (market.clobTokenIds.trim().startsWith('[')) {
        return JSON.parse(market.clobTokenIds);
      }
      return [market.clobTokenIds];
    }
    return [String(market.clobTokenIds)];
  } catch (e) {
    return [];
  }
};

/**
 * Gets the price metadata for a specific outcome index in a market.
 */
export const getMarketPriceUpdate = (market: EventMarket | undefined, index: number) => {
  if (!market || !market.priceMetadata || !market.clobTokenIds) return null;
  const tokenIds = getMarketTokenIds(market);
  const tokenId = tokenIds[index];
  if (!tokenId) return null;
  return market.priceMetadata[tokenId as string];
};

/**
 * Common labels for main game market groups that should be prioritized and have no sub-headers
 */
export const MAIN_MARKET_LABELS = [
  'game lines',
  'game line',
  'main',
  '1st half',
  'first half',
  'total sets',
  'match totals',
];

/**
 * Checks if a market group is one of the main game-line categories
 */
export const isMainMarketGroup = (label: string): boolean => {
  return MAIN_MARKET_LABELS.includes((label || '')?.toLowerCase());
};

/**
 * Sorts market category labels so that main lines appear first
 */
export const sortMarketTabs = (labels: string[] = []): string[] => {
  return [...labels].sort((a, b) => {
    const aLower = (a || '')?.toLowerCase();
    const bLower = (b || '')?.toLowerCase();
    const aIdx = MAIN_MARKET_LABELS.indexOf(aLower);
    const bIdx = MAIN_MARKET_LABELS.indexOf(bLower);

    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return 0;
  });
};

/**
 * Processes a market group into a clean list of card data objects.
 * Handles specific sorting for Game Lines and formats market titles.
 */
/**
 * Shared helper to determine the core theme of a market type (Moneyline, Spread, Total)
 */
const getCoreType = (type: string): string | null => {
  const t = (type || '')?.toLowerCase();
  if (t.includes('moneyline')) return 'moneyline';
  if (t.includes('spread')) return 'spread';
  if (t.includes('total')) return 'total';
  return null;
};

const MARKET_PRIORITY: Record<string, number> = {
  moneyline: 1,
  spread: 2,
  totals: 3,
  total: 3,
};

/**
 * Shared helper to format market titles consistently based on group context
 */
const getDisplayTitle = (market: any, groupLabel: string) => {
  const type = getMarketType(market);
  const coreType = getCoreType(type);
  const label = groupLabel?.toLowerCase();

  // 1. Group-specific overrides (Main Lines)
  if (label.includes('game line')) {
    if (coreType === 'moneyline') return 'Moneyline';
    if (coreType === 'spread') {
      const perCard = (market.groupItemTitle || '').trim() || (market.question || '').trim();
      if (perCard) return perCard;
      return 'Spreads';
    }
    if (coreType === 'total') return 'Totals';
  }

  if (label === 'match totals' && coreType === 'total') return 'Total Games';
  if (label === 'total sets' && coreType === 'total') return 'Total Sets';

  // 2. Player Props (Points, Rebounds, Assists, etc.)
  const propTypes = ['points', 'rebounds', 'assists', 'blocks', 'steals', 'threes'];
  if (propTypes.includes(type) || propTypes.some((p) => type.includes(p))) {
    const title = market.groupItemTitle || market.question || '';
    if (title.includes(':')) {
      return extractPlayerName(title);
    }
  }

  // 3. Dynamic Slug-based formatting (Tennis winner etc)
  let formatted = type.replace(/^[a-z]+_/i, '');

  if (label.includes('2nd half') || label.includes('second half')) {
    formatted = formatted.replace(/second_half_/gi, '').replace(/2nd_half_/gi, '');
  }

  formatted = formatted
    .replace(/_/g, ' ')
    .replace(/\bfirst\b/gi, '1st')
    .replace(/\bsecond\b/gi, '2nd')
    .replace(/\bthird\b/gi, '3rd')
    .replace(/\bfourth\b/gi, '4th')
    .replace(/\bwinner\b/gi, 'Winner')
    .replace(/\btotals?\b/gi, 'Total Games')
    .replace(/\bspreads?\b/gi, 'Spreads');

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/**
 * Generic processor for a single market group.
 * Can be customized by passing options or used as a base.
 */
/**
 * Processors specialized for each layout pattern
 */
export const processFlatMarkets = (group: any, event?: any) => {
  if (!group || !group.markets || !group.sportsMarketTypes) return [];

  const processedItems: any[] = [];
  const groupLabel = group.label || '';

  group.sportsMarketTypes.forEach((type: string) => {
    const lowerType = type?.toLowerCase();
    const coreType = getCoreType(lowerType);

    const matchingMarkets = group.markets.filter((m: any) => getMarketType(m) === lowerType);

    if (matchingMarkets.length === 0) return;

    if (coreType === 'moneyline' || coreType === 'total') {
      const bestMarket = matchingMarkets[0];
      processedItems.push({
        title: getDisplayTitle(bestMarket, groupLabel),
        marketType: lowerType,
        market: bestMarket,
      });
    } else if (coreType === 'spread') {
      matchingMarkets.forEach((m: any) => {
        processedItems.push({
          title: getDisplayTitle(m, groupLabel),
          marketType: lowerType,
          market: m,
        });
      });
    } else {
      matchingMarkets.forEach((m: any) => {
        processedItems.push({
          title: getDisplayTitle(m, groupLabel),
          marketType: lowerType,
          market: m,
        });
      });
    }
  });

  return processedItems.sort((a: any, b: any) => {
    const typeA = getCoreType(a.marketType);
    const typeB = getCoreType(b.marketType);
    const pA = typeA ? MARKET_PRIORITY[typeA] : 99;
    const pB = typeB ? MARKET_PRIORITY[typeB] : 99;
    return pA - pB;
  });
};

export const processGroupedMarkets = (group: any, event?: any) => {
  if (!group || !group.markets || !group.sportsMarketTypes) return [];

  const processedItems: any[] = [];
  const groupLabel = group.label || '';

  // For each allowed type in this group
  group.sportsMarketTypes.forEach((type: string) => {
    const lowerType = type?.toLowerCase();
    const coreType = getCoreType(lowerType);

    // Find all markets of this type
    const matchingMarkets = group.markets.filter((m: any) => getMarketType(m) === lowerType);

    if (matchingMarkets.length === 0) return;

    // ML + totals: one card (line slider when multiple markets). Spreads: one card per
    // distinct handicap market (e.g. soccer home vs away framing) — legacy path without lineGroups.
    if (coreType === 'moneyline' || coreType === 'total') {
      const bestMarket = matchingMarkets[0];
      processedItems.push({
        title: getDisplayTitle(bestMarket, groupLabel),
        marketType: lowerType,
        market: bestMarket,
      });
    } else if (coreType === 'spread') {
      matchingMarkets.forEach((m: any) => {
        processedItems.push({
          title: getDisplayTitle(m, groupLabel),
          marketType: lowerType,
          market: m,
        });
      });
    } else {
      // For props (Points, etc.), we want one card per distinct market instance (player)
      matchingMarkets.forEach((m: any) => {
        processedItems.push({
          title: getDisplayTitle(m, groupLabel),
          marketType: lowerType,
          market: m,
        });
      });
    }
  });

  return processedItems.sort((a: any, b: any) => {
    const typeA = getCoreType(a.marketType);
    const typeB = getCoreType(b.marketType);
    const pA = typeA ? MARKET_PRIORITY[typeA] : 99;
    const pB = typeB ? MARKET_PRIORITY[typeB] : 99;
    return pA - pB;
  });
};

export const processTabbedMarkets = (group: any, event?: any) => {
  if (!group || !group.markets || !group.sportsMarketTypes) return [];

  const processedItems: any[] = [];
  const groupLabel = group.label || '';

  group.sportsMarketTypes.forEach((type: string) => {
    const lowerType = type?.toLowerCase();
    const coreType = getCoreType(lowerType);

    const matchingMarkets = group.markets.filter((m: any) => getMarketType(m) === lowerType);

    if (matchingMarkets.length === 0) return;

    if (coreType === 'moneyline' || coreType === 'total') {
      const bestMarket = matchingMarkets[0];
      processedItems.push({
        title: getDisplayTitle(bestMarket, groupLabel),
        marketType: lowerType,
        market: bestMarket,
      });
    } else if (coreType === 'spread') {
      matchingMarkets.forEach((m: any) => {
        processedItems.push({
          title: getDisplayTitle(m, groupLabel),
          marketType: lowerType,
          market: m,
        });
      });
    } else {
      matchingMarkets.forEach((m: any) => {
        processedItems.push({
          title: getDisplayTitle(m, groupLabel),
          marketType: lowerType,
          market: m,
        });
      });
    }
  });

  return processedItems.sort((a: any, b: any) => {
    const typeA = getCoreType(a.marketType);
    const typeB = getCoreType(b.marketType);
    const pA = typeA ? MARKET_PRIORITY[typeA] : 99;
    const pB = typeB ? MARKET_PRIORITY[typeB] : 99;
    return pA - pB;
  });
};
/**
 * Calculates a signed point spread line (e.g. +7.5 or -7.5) for a specific team.
 */
export const getSpreadLine = (
  market: EventMarket | undefined,
  teamName: string | undefined,
  isAway: boolean,
): string | undefined => {
  const line = getCanonicalMarketLine(market);
  if (line == null) return undefined;
  const outcomes = getOutcomes(market);
  const idx = findOutcomeIndexForTeam(outcomes, teamName);
  const teamIdx = idx !== -1 ? idx : isAway ? 1 : 0;
  const val = teamIdx === 0 ? Number(line) : -Number(line);
  return val > 0 ? `+${val}` : String(val);
};

/**
 * Robustly finds a Draw outcome within an event.
 * Checks for a standalone "Draw" market or a 3-way moneyline market.
 */
export const resolveDrawOutcome = (
  event: EventResponse,
): {
  pricing: { price: string; id?: string; marketId?: string } | undefined;
  outcomes?: EventOutcome[];
} => {
  const drawMarket = findMarket(event, 'draw');
  if (!drawMarket) return { pricing: undefined };

  const outcomes = getOutcomes(drawMarket);
  let idx = findOutcomeIndexForTeam(outcomes, 'draw');
  if (idx === -1) idx = findOutcomeIndexForTeam(outcomes, 'empate');
  const outcome = idx !== -1 ? outcomes[idx] : outcomes[0];

  if (!outcome) return { pricing: undefined };

  return {
    pricing: {
      price: formatSportPrice(outcome.bestAsk || outcome.price),
      id: outcome.id,
      marketId: drawMarket.id,
    },
    outcomes,
  };
};

/**
 * Returns true if the event belongs to the Esports category
 * (determined by the presence of a tag with slug "esports").
 */
export const isEsportsEvent = (event: EventResponse): boolean =>
  event.tags?.some((t) => t.slug === 'esports') ?? false;

// Polymarket's Gamma `live` flag can go stale when a fixture is postponed and
// rescheduled — it isn't always cleared for the new date, so the API can hand
// back a game that's actually weeks away still marked `live: true`. Nothing
// scheduled more than a few hours out can genuinely be live right now, so this
// is a defensive sanity check independent of that flag.
const LIVE_FUTURE_GRACE_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Returns true only if an event flagged `live` by the API is plausibly
 * actually live right now — excludes postponed games and anything whose
 * real scheduled start is still hours/weeks away.
 */
export const isActuallyLive = (event: EventResponse): boolean => {
  const isPostponed = event.period === 'POST' || event.status?.toLowerCase().includes('postponed');
  if (isPostponed) return false;

  // gameStartTime is the authoritative schedule (updated on postponement/reschedule).
  // gameDate/eventDate/startDate are derived from the original slug/date and stay
  // stale when a game is pushed out, so check gameStartTime first.
  const scheduledAt = event.gameStartTime || event.gameDate || event.eventDate || event.startDate;
  if (scheduledAt) {
    const scheduledTime = new Date(scheduledAt).getTime();
    if (!Number.isNaN(scheduledTime) && scheduledTime - Date.now() > LIVE_FUTURE_GRACE_MS) {
      return false;
    }
  }
  return true;
};
